import type { LanguagePack } from './types'

export const en: LanguagePack = {
  navSaved: 'Saved',
  navInfo: 'Info',
  homeTitle: 'Take a photo of your document',
  homeHint: 'Samjho will tell you what it says and what you need to do next.',
  takePhoto: 'Take photo',
  uploadFile: 'Upload photo or PDF',
  supportedFiles: 'Photo (JPG, PNG, WEBP) or PDF · up to 10 MB',
  chooseLanguage: 'Explain in',
  privacyLine: 'Your document is read on this phone. It is not saved unless you tap "Save".',

  previewTitle: 'Is the photo OK?',
  rotate: 'Rotate',
  crop: 'Crop',
  cropDone: 'Done',
  retake: 'Take again',
  readNow: 'Read it',
  docLanguageLabel: 'Which language is the document written in?',
  docLanguageHint: 'English is always read too.',

  stepCheck: 'Checking the photo…',
  stepRead: 'Reading the document…',
  stepExtract: 'Finding the important details…',
  stepExplain: 'Explaining in English…',
  firstTimeNote: 'The first time takes a little longer while the reading tool downloads.',
  cancel: 'Cancel',

  errBadImage: 'The photo is a bit unclear. Please take another, clearer photo.',
  errNoText: 'Samjho could not find any written text on this document.',
  errUnclearPart: 'Samjho could not read this part properly.',
  errUnsupportedLanguage: 'This language is not supported yet.',
  errLongDocument: 'This document is quite long. Please select the important pages first.',
  errFileType: 'Please choose a photo (JPG, PNG, WEBP) or a PDF.',
  errFileSize: 'This file is larger than 10 MB. Please choose a smaller file.',
  errGeneric: 'Something went wrong. Please try again.',
  errOcrDownload: 'The reading tool could not be downloaded. Please connect to the internet once and try again.',
  warnDark: 'The photo looks dark. A photo in good light reads better.',
  warnBlurry: 'The photo looks blurry. Hold the phone steady and take it again for a better result.',
  warnSmall: 'The photo is very small. Take it closer to the document for a better result.',
  tryAgain: 'Try again',

  pickPagesHint: (max) => `Choose up to ${max} pages.`,
  pageLabel: (n) => `Page ${n}`,
  readSelectedPages: (n) => `Read ${n} ${n === 1 ? 'page' : 'pages'}`,

  typeSure: (name) => `This looks like ${name}.`,
  typeMaybe: (name) => `This may be ${name}, but Samjho is not fully sure.`,
  typeUnknown: 'Samjho is not fully sure what type of document this is.',
  docTypes: {
    income_certificate: { name: 'an income certificate', about: 'It records the income of a person or family, as per government records.' },
    caste_certificate: { name: 'a caste certificate', about: 'It records which caste or category a person belongs to.' },
    domicile_certificate: { name: 'a domicile (residence) certificate', about: 'It records where a person lives permanently.' },
    birth_certificate: { name: 'a birth certificate', about: 'It is the official record of a birth.' },
    death_certificate: { name: 'a death certificate', about: 'It is the official record of a death.' },
    electricity_bill: { name: 'an electricity bill', about: 'It shows the electricity used and the money to be paid.' },
    water_bill: { name: 'a water bill', about: 'It shows the water charges to be paid.' },
    ration_card: { name: 'a ration card or ration notice', about: 'It is about getting ration from the government shop.' },
    aadhaar: { name: 'an Aadhaar document', about: 'It is about an Aadhaar number or Aadhaar details.' },
    pan_card: { name: 'a PAN document', about: 'It is about a PAN (tax ID) number.' },
    bank_notice: { name: 'a bank letter', about: 'It is a letter or notice from a bank.' },
    loan_notice: { name: 'a loan notice', about: 'It is about a loan or money owed.' },
    income_tax_notice: { name: 'an income tax notice', about: 'It is from the Income Tax Department.' },
    pension: { name: 'a pension letter', about: 'It is about a pension.' },
    court_notice: { name: 'a court or legal notice', about: 'It is from a court or about a legal matter. Take it seriously.' },
    land_record: { name: 'a land record', about: 'It is about land ownership or land details.' },
    property_tax: { name: 'a property tax bill', about: 'It is about tax on a house or property.' },
    scholarship: { name: 'a scholarship letter', about: 'It is about a scholarship for studies.' },
    farmer_scheme: { name: 'a farmer scheme letter', about: 'It is about a scheme or help for farmers.' },
    school_letter: { name: 'a school letter', about: 'It is from a school or about studies.' },
    marksheet: { name: 'a marksheet or result', about: 'It shows exam marks or a result.' },
    insurance: { name: 'an insurance document', about: 'It is about an insurance policy.' },
    traffic_challan: { name: 'a traffic challan', about: 'It is a fine for a traffic or vehicle rule.' },
    job_notice: { name: 'a job or exam notice', about: 'It is about government jobs, recruitment, or an exam for them.' },
    application_receipt: { name: 'an application receipt', about: 'It shows that an application was received.' },
    application_decision: { name: 'a letter about an application', about: 'It gives a decision or update about an application.' },
    government_notice: { name: 'a government notice', about: 'It is a notice or order from a government office.' },
    lab_report: { name: 'a medical test report', about: 'It shows the results of blood or other medical tests.' },
    rental_agreement: { name: 'a rent agreement', about: 'It is an agreement between a landlord and a tenant.' },
    loan_agreement: { name: 'a loan agreement', about: 'It sets the terms of a loan: interest, EMI and charges.' },
    contract: { name: 'an agreement', about: 'It is an agreement with terms both sides must follow.' },
    unknown: { name: 'a document', about: '' }
  },

  seedhiBaat: 'In simple words',
  docSays: 'Document says',
  samjhoSays: 'Samjho explains',
  unclearText: 'Text unclear — please check the original document.',

  aboutPerson: (name) => `This document is about ${name}.`,
  issuedBy: (authority) => `It is from: ${authority}.`,
  status: {
    approved: 'The application has been approved.',
    rejected: 'The application has been rejected.',
    pending: 'The application is still in process. No decision yet.',
    cancelled: 'This has been cancelled.'
  },
  dateSentence: {
    deadline: (d) => `Last date: ${d}.`,
    due: (d) => `Pay by: ${d}.`,
    hearing: (d) => `The document gives this date to be present: ${d}.`,
    issue: (d) => `Issued on ${d}.`,
    validity: (d) => `Valid until ${d}.`,
    birth: (d) => `Date of birth written: ${d}.`,
    other: (d) => `A date is written: ${d}.`
  },
  daysLeft: (days) => (days === 1 ? '1 day is left.' : `${days} days are left.`),
  dueToday: 'This date is today.',
  datePassed: 'Note: this date has already passed.',
  amountSentence: {
    payable: (a) => `Amount to pay: ${a}.`,
    income: (a) => `Income written: ${a}.`,
    fine: (a) => `Fine or late fee written: ${a}.`,
    fee: (a) => `Fee written: ${a}.`,
    sanctioned: (a) => `Amount approved or sent: ${a}.`,
    other: (a) => `An amount is written: ${a}.`
  },
  actionsPointer: 'The document asks you to do something. See "What you need to do" below.',
  noActionFound: 'The document does not clearly ask you to do anything.',
  keepSafe: 'Keep this document safe.',
  doBefore: (d) => `Do this before ${d}.`,
  bringDocs: (list) => `Keep these papers ready: ${list}.`,
  contactFor: (c) => `For questions, contact: ${c}.`,
  warningPointer: 'The document contains a warning. Please read it carefully.',
  rejectedNext: 'If you think this is a mistake, ask the office that sent this letter.',
  lowQuality: 'The photo was not very clear, so some details may be read wrongly. Please check with the original.',

  cardTodo: 'What you need to do',
  cardDates: 'Important dates',
  cardAmount: 'Amount',
  cardWhere: 'Where to go',
  cardWarnings: 'Be careful',
  cardDocs: 'Papers needed',
  cardPerson: 'About whom',
  cardFrom: 'Sent by',
  cardNumbers: 'Numbers to keep',
  cardContact: 'Contact',
  refKinds: {
    application: 'Application no.',
    certificate: 'Certificate no.',
    consumer: 'Consumer no.',
    account: 'Account no.',
    case: 'Case no.',
    reference: 'Letter no.',
    registration: 'Registration no.',
    receipt: 'Receipt no.',
    other: 'Number'
  },
  requiredDocNames: {
    aadhaar: 'Aadhaar card',
    pan: 'PAN card',
    ration_card: 'Ration card',
    voter_id: 'Voter ID',
    passbook: 'Bank passbook',
    photo: 'Photo',
    income_certificate: 'Income certificate',
    caste_certificate: 'Caste certificate',
    domicile_certificate: 'Domicile certificate',
    birth_certificate: 'Birth certificate',
    land_record: 'Land record',
    electricity_bill: 'Electricity bill',
    address_proof: 'Address proof',
    id_proof: 'ID proof',
    affidavit: 'Affidavit',
    application_form: 'Application form'
  },

  voiceDownloadNote: 'First time: downloading the voice (about 58 MB). After that it works offline.',

  listen: 'Listen',
  pause: 'Pause',
  resume: 'Continue',
  stop: 'Stop',
  noVoice: 'This phone does not have an English voice.',
  noVoiceHint: 'You can add a voice in your phone settings (Text-to-speech), or listen in another language.',
  listenInHindi: 'Listen in Hindi',
  listenInEnglish: 'Listen in English',
  noSpeechSupport: 'This browser cannot read aloud.',

  disclaimer: 'Samjho simplifies your document. Always verify important details with the original document or the issuing authority. Samjho is not a government service and this is not legal advice.',
  fullText: 'What Samjho read',
  fullTextHint: 'Highlighted lines could not be read clearly.',
  newDocument: 'New document',
  save: 'Save on this phone',
  saved: 'Saved on this phone',
  share: 'Share',
  copied: 'Copied',
  translateLines: 'Show the document lines in English',
  helperMode: 'Helper mode',
  helperModeHint: 'For people who read papers for others (CSC, NGO, volunteers). Saving is turned off, and "Next person" clears everything.',
  nextPerson: 'Next person',
  listenMode: 'Listen mode',
  listenModeHint: 'Samjho reads the explanation aloud by itself as soon as it is ready.',
  settingsTitle: 'Settings',
  shareVoiceNote: 'Send as voice note',
  makingVoiceNote: 'Making the voice note…',
  voiceNoteFailed: 'Could not make the voice note on this phone.',
  translateOtherLines: 'Show the document\'s lines in English',
  translateAiNote: 'Lines that are not in English are translated by an AI service (Groq).',
  translateConsent: 'To translate, these lines will be sent over the internet to the Samjho server. They are not saved. Numbers like Aadhaar and phone are removed first.',
  translateConfirm: 'Yes, translate',
  translating: 'Translating…',
  translateNote: 'Machine translation — may have mistakes.',
  translateFailed: 'Translation is not available right now.',
  retryRotated: 'Rotate photo and read again',

  addToCalendar: 'Add reminder to calendar',
  askTitle: 'Ask about this document',
  askHint: 'Ask in your own words. Samjho answers from your document — and if it isn’t there, only from official government websites.',
  askPlaceholder: 'Type or speak your question…',
  askSend: 'Ask',
  askMic: 'Speak your question',
  askListening: 'Listening… speak now',
  askWebToggle: 'If needed, also check official government websites',
  askConsent: 'To answer, the text of this document and your question will be sent over the internet to the Samjho server and an AI service (Groq). Aadhaar, PAN, phone numbers and email are removed first. Nothing is saved.',
  askOfficialOnly: 'Only official websites are checked, like:',
  askConsentConfirm: 'OK, ask',
  askThinkingDoc: 'Reading your document',
  askThinkingWeb: 'Checking official websites',
  askFromDoc: 'From your document',
  askFromWeb: 'From official websites',
  askNotFound: 'Samjho could not find this in your document or on official websites. Please ask the office that sent this document.',
  askUnverified: 'Samjho could not match this to an exact line. Please check the document.',
  askSources: 'Sources',
  askOfficial: 'official',
  askFailed: 'Could not get an answer right now. Please try again.',
  askBusy: 'Too many questions right now. Please wait a minute and try again.',
  askAiNote: 'Answers are written by AI and may have mistakes. Check important things with the office.',
  askTryNext: 'You can also ask:',
  askJump: 'Ask a question',
  askSuggest: {
    fillForm: 'How do I fill this form? Explain each field.',
    first: 'What should I do first?',
    deadline: 'What happens if I miss the last date?',
    pay: 'How and where can I pay this?',
    papers: 'Where can I get these papers?',
    rejected: 'Why was it rejected, and what can I do now?',
    meaning: 'Explain this document in very simple words.',
    contact: 'Whom should I contact about this?',
    schemes: 'Which government schemes could I be eligible for, based on this document?'
  },

  helpTitle: 'Get help',
  nearbyTitle: 'Nearest office',
  nearbyNote: 'Opens the map near you. Your location goes only to the map — Samjho does not see or save it.',
  locating: 'Finding your location…',
  schemesTitle: 'Government schemes',
  schemesText: 'Samjho can check official scheme websites for schemes that may fit this document.',
  schemesButton: 'Find schemes for me',
  legalTitle: 'Free legal help',
  legalText: 'You may be able to get a free lawyer from the Legal Services Authority — for example women, children, SC/ST, workers, people with disabilities and people with low income.',
  legalCall: (n) => `Call ${n} (free)`,
  legalNearby: 'Nearest Legal Services Authority',
  legalMore: 'More legal help',
  legalAskConsent: 'Your question will be sent over the internet to the Samjho server and an AI service (Groq), which look for the answer only on official government websites. Aadhaar, PAN, phone numbers and email are removed first. Nothing is saved.',
  navLegal: 'Legal help'
}
